import React, { useState, useEffect } from 'react';
import { trainingTypesAPI } from '../../services/api';
import '../../styles/AdminStyles.css';

const DEFAULT_FORM = {
  name: '',
  compatibility_group: 1,
  sort_order: 0,
  is_active: true,
};

function AdminTrainingTypes() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(DEFAULT_FORM);

  const fetchRows = async () => {
    setError('');
    try {
      setLoading(true);
      const data = await trainingTypesAPI.getAllAdmin();
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      setRows([]);
      setError(
        e.response?.data?.error ||
          'לא ניתן לטעון סוגי אימון. ודאו שהמיגרציה הורצה בשרת (migration_training_types.sql).'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(DEFAULT_FORM);
    setShowModal(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      name: row.name,
      compatibility_group: row.compatibility_group,
      sort_order: row.sort_order,
      is_active: !!Number(row.is_active),
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditing(null);
    setForm(DEFAULT_FORM);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await trainingTypesAPI.update(editing.id, {
          ...form,
          compatibility_group: parseInt(form.compatibility_group, 10),
          sort_order: parseInt(form.sort_order, 10) || 0,
        });
      } else {
        await trainingTypesAPI.create({
          ...form,
          compatibility_group: parseInt(form.compatibility_group, 10),
          sort_order: parseInt(form.sort_order, 10) || 0,
        });
      }
      closeModal();
      fetchRows();
    } catch (err) {
      alert(err.response?.data?.error || 'שגיאה בשמירה');
    }
  };

  const handleDelete = async (row) => {
    if (
      !window.confirm(
        `למחוק את סוג האימון "${row.name}"? (אפשר רק אם אין משתמשים או שעות שמשתמשים בו)`
      )
    ) {
      return;
    }
    try {
      await trainingTypesAPI.delete(row.id);
      fetchRows();
    } catch (err) {
      alert(err.response?.data?.error || 'לא ניתן למחוק — כנראה בשימוש');
    }
  };

  if (loading && rows.length === 0 && !error) {
    return (
      <div className="admin-loading">
        <div className="admin-spinner"></div>
        <p>טוען סוגי אימון...</p>
      </div>
    );
  }

  return (
    <div className="admin-users">
      <div className="admin-header">
        <div>
          <h1>סוגי אימון</h1>
          <p>
            קבוצת תאימות: סוגים עם אותו מספר — לקוח יכול לקבוע ביניהם (למשל בר ומכשיר = קבוצה 1).
          </p>
        </div>
        <button type="button" className="admin-button-primary" onClick={openCreate}>
          ➕ סוג חדש
        </button>
      </div>

      {error && (
        <div style={{ marginBottom: '1rem', color: '#c62828', maxWidth: '720px' }}>{error}</div>
      )}

      <div className="admin-table-container">
        <table className="admin-table">
          <thead>
            <tr>
              <th>שם</th>
              <th>קבוצת תאימות</th>
              <th>סדר</th>
              <th>פעיל</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td>
                  <span className="admin-badge">{r.name}</span>
                </td>
                <td>{r.compatibility_group}</td>
                <td>{r.sort_order}</td>
                <td>{Number(r.is_active) ? 'כן' : 'לא'}</td>
                <td>
                  <button
                    type="button"
                    className="admin-button-secondary"
                    onClick={() => openEdit(r)}
                  >
                    עריכה
                  </button>{' '}
                  <button
                    type="button"
                    className="admin-button-secondary"
                    onClick={() => handleDelete(r)}
                  >
                    מחיקה
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="admin-modal-overlay" onClick={closeModal}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <h2>{editing ? 'עריכת סוג אימון' : 'סוג אימון חדש'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="admin-form-group">
                <label>שם (כפי שיופיע במערכת) *</label>
                <input
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  required
                  maxLength={80}
                />
              </div>
              <div className="admin-form-group">
                <label>קבוצת תאימות *</label>
                <input
                  type="number"
                  name="compatibility_group"
                  value={form.compatibility_group}
                  onChange={handleChange}
                  min="1"
                  required
                />
                <small style={{ display: 'block', marginTop: '6px', opacity: 0.85 }}>
                  סוגים עם אותו מספר — לקוח יכול לקבוע ביניהם (למשל בר ומכשיר = 1).
                </small>
              </div>
              <div className="admin-form-group">
                <label>סדר תצוגה</label>
                <input
                  type="number"
                  name="sort_order"
                  value={form.sort_order}
                  onChange={handleChange}
                />
              </div>
              <div className="admin-form-group">
                <label>
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={form.is_active}
                    onChange={handleChange}
                  />{' '}
                  פעיל (ניתן לבחור בטפסים)
                </label>
              </div>
              <div className="admin-modal-footer">
                <button type="button" className="admin-button-secondary" onClick={closeModal}>
                  ביטול
                </button>
                <button type="submit" className="admin-button-primary">
                  שמור
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminTrainingTypes;
